import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {dropDownOption, roles, rolesLabelMap} from "../../../../constants/common.constants";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {SharedService} from "../../../../core/shared.service";
import {CommonService} from "../../../../services/common.service";
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../../../../state/app.state";
import {
  FetchProfileByUserIdRequest,
  FetchProfileRequest
} from "../../../../core/shared.actions";
import {firstValueFrom, Subject, takeUntil} from "rxjs";

@Component({
  selector: 'app-adult-details',
  templateUrl: './adult-details.component.html',
  styleUrls: ['./adult-details.component.css']
})
export class AdultDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() userDetails;
  @Input() loading;
  @Input() loggedInUserRole: number;
  @Input() isEditMode: boolean = false;
  @Input() hasUserId: boolean = false;
  @Output() handleClick = new EventEmitter();

  protected readonly rolesLabelMap = rolesLabelMap;
  protected readonly roles = roles;

  relationshipFilterList: any[] = [];
  householdFilterList: any[] = [];
  relationshipFilterLoading: boolean = false;
  householdFilterLoading: boolean = false;
  isLoading: boolean = false;
  private dropdownDataFetched: boolean = false;

  forms: { [adultId: number]: FormGroup } = {};

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private sharedService: SharedService,
    private commonService: CommonService,
    private store: Store<fromRoot.State>
  ) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['userDetails'] && this.userDetails && !this.dropdownDataFetched) {
      this.fetchDropdownData();
      this.dropdownDataFetched = true;
    }
    if (changes['isEditMode'] && this.isEditMode) {
      this.initForms();
    }
  }

  initForms() {
    this.forms = {};
    if (this.userDetails?.adults) {
      this.userDetails.adults.forEach(adult => {
        const isOther = adult.relation?.key === dropDownOption.others;
        this.forms[adult.id] = new FormGroup({
          relation: new FormControl(adult.relation?.key ?? '', [Validators.required]),
          otherRelationText: new FormControl(
            isOther ? (adult.relation?.value || '') : '',
            isOther ? [Validators.required] : []
          ),
          household: new FormControl(adult.household?.key ?? '', [Validators.required]),
        });

        this.forms[adult.id].get('relation').valueChanges
          .pipe(takeUntil(this.onDestroy))
          .subscribe(value => {
            const otherControl = this.forms[adult.id].get('otherRelationText');
            if (value === dropDownOption.others) {
              otherControl.setValidators([Validators.required]);
            } else {
              otherControl.setValue('');
              otherControl.clearValidators();
            }
            otherControl.updateValueAndValidity();
          });
      });
    }
  }

  fetchDropdownData() {
    this.relationshipFilterLoading = true;
    this.householdFilterLoading = true;

    this.sharedService.fetchRelationsFilter()
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (response) => {
          this.relationshipFilterList = response?.data?.list || [];
          this.relationshipFilterLoading = false;
        },
        error: () => {
          this.relationshipFilterLoading = false;
        }
      });

    const obParams: any = {questionGroup: 'household', forRole: this.roles.adult};
    if (this.loggedInUserRole !== this.roles.participant) {
      obParams.participantId = this.userDetails?.id;
    }
    this.sharedService.fetchObQuestions(obParams)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (response) => {
          const householdQuestion = response?.data?.[0];
          this.householdFilterList = householdQuestion?.options || [];
          this.householdFilterLoading = false;
        },
        error: () => {
          this.householdFilterLoading = false;
        }
      });
  }

  save() {
    const adults = this.userDetails?.adults || [];
    const updatePromises = [];

    for (const adult of adults) {
      const form = this.forms[adult.id];
      if (!form || form.invalid || !form.dirty) continue;

      const {relation, otherRelationText, household} = form.getRawValue();
      const payload: any = {
        participantId: this.userDetails.id,
        adultId: adult.id,
        relation,
        household
      };
      if (relation === dropDownOption.others) {
        payload.otherRelationText = otherRelationText;
      }

      updatePromises.push(
        firstValueFrom(this.sharedService.updateAdultDetails(payload))
      );
    }

    if (updatePromises.length === 0) {
      this.handleClick.emit();
      return;
    }

    this.isLoading = true;
    Promise.all(updatePromises)
      .then(() => {
        this.isLoading = false;
        this.commonService.notification('Adult details updated successfully.', 'success');
        this.handleClick.emit();
        // Re-fetch profile to refresh data
        if (this.hasUserId) {
          this.store.dispatch(FetchProfileByUserIdRequest({payload: {userId: this.userDetails.id}}));
        } else {
          this.store.dispatch(FetchProfileRequest());
        }
      })
      .catch(() => {
        this.isLoading = false;
      });
  }

  get isAnyFormDirty(): boolean {
    return Object.values(this.forms).some(form => form.dirty);
  }

  close() {
    this.handleClick.emit();
  }

  editAdultDetails() {
    this.handleClick.emit();
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
